/**
 * "Just this session" (docs/security.md "Session devices"): sign in on a browser without trusting
 * it. The device signing key is generated non-extractable and held in memory only — every Viko
 * IndexedDB read/write in this page goes to a page-lifetime map (lib/idb.ts), so the key, the
 * passkey pins and the content keys disappear with the tab. The passkey endorsement is marked
 * `scope: 'session'` and lasts at most 12 h; the hub ends the sign-in with it.
 *
 * Revocation is best effort (sign-out, pagehide); the endorsement's expiry, enforced by the hub
 * and every daemon, is the real guarantee. A non-secret marker in sessionStorage (the device id
 * only) lets a reload of this tab end the leftover hub session and land on sign-in.
 */
import type { Api } from './api.js'
import { _resetDeviceCache } from './device-key.js'
import { idb } from './idb.js'

const MARK = 'viko.session-only'

function storage(): Storage | undefined {
  try {
    return globalThis.sessionStorage
  } catch {
    return undefined
  }
}

/** True while this page runs "Just this session" (memory-only keys). */
export function isSessionOnly(): boolean {
  return idb.memoryOnly
}

/** Switch this page to memory-only keys before the device key is generated. */
export function startSessionOnly(): void {
  if (!idb.memoryOnly) {
    idb.enterMemoryOnly()
    // A persisted (untrusted or expired) key must not be reused: the next key is memory-only.
    _resetDeviceCache()
  }
}

/** The session-only choice was abandoned before any key was endorsed: back to normal storage. */
export function abandonSessionOnly(): void {
  idb.dropMemory()
  _resetDeviceCache()
}

/**
 * A trusted sign-in or "Trust this device" on a page still in memory-only mode (a session sign-in
 * whose endorsement expired, or a session attempt that failed after endorsing): leave memory-only
 * mode FIRST, so the trusted device key, its endorsement and its passkey pins go to IndexedDB.
 * Otherwise they would live only in this tab and vanish on reload — a "trusted" browser left with
 * no key, then re-endorsed with no pins. The session key is wiped; its endorsement expires (≤ 12 h).
 */
export function leaveSessionOnlyForTrust(): void {
  if (!idb.memoryOnly) return
  forget()
  idb.dropMemory()
  _resetDeviceCache()
}

/** Remember (for this tab only) which session device to end if the page reloads. */
export function rememberSessionDevice(deviceKeyId: string): void {
  try {
    storage()?.setItem(MARK, JSON.stringify({ deviceKeyId }))
  } catch {
    // Private mode: the pagehide revocation and the 12 h expiry still apply.
  }
}

function remembered(): { deviceKeyId: string } | undefined {
  try {
    const raw = storage()?.getItem(MARK)
    const v = raw ? (JSON.parse(raw) as { deviceKeyId?: unknown }) : undefined
    return typeof v?.deviceKeyId === 'string' ? { deviceKeyId: v.deviceKeyId } : undefined
  } catch {
    return undefined
  }
}

function forget(): void {
  try {
    storage()?.removeItem(MARK)
  } catch {
    // ignore
  }
}

/**
 * Sign-out on a session-only page: revoke the session device (which ends its sign-in at the hub),
 * then wipe the in-memory keys and leave memory-only mode. Never throws.
 */
export async function endSessionOnly(api: Pick<Api, 'revokeDevice'>, deviceKeyId?: string) {
  const id = deviceKeyId ?? remembered()?.deviceKeyId
  if (id) await api.revokeDevice(id).catch(() => {})
  forget()
  idb.dropMemory()
  _resetDeviceCache()
}

/**
 * The hub ended this tab's sign-in (removed from Devices on another browser, or expired): wipe
 * the memory-only keys now instead of when the tab closes, and leave memory-only mode. The device
 * is already revoked, so nothing is sent. Never throws.
 */
export function wipeSessionOnly(): void {
  if (!idb.memoryOnly) return
  forget()
  idb.dropMemory()
  _resetDeviceCache()
}

/**
 * Page load: a previous page in this tab ran "Just this session" and its keys are gone. Revoke
 * that device and sign the leftover hub session out, so the user lands on sign-in. Returns true
 * when it did so.
 */
export async function endStaleSessionOnly(api: Pick<Api, 'revokeDevice' | 'logout'>) {
  if (idb.memoryOnly) return false
  const stale = remembered()
  if (!stale) return false
  forget()
  await api.revokeDevice(stale.deviceKeyId).catch(() => {})
  await api.logout().catch(() => {})
  return true
}

/**
 * Best-effort revocation when the page goes away (tab closed, reload, navigation). `keepalive`
 * lets the request outlive the page; the hub then ends the bound sign-in. A bfcache restore
 * (pageshow persisted) keeps working until the endorsement's own expiry is reached... but the
 * device is revoked, so the app routes back to sign-in on its next request.
 */
export function installSessionOnlyUnload(
  revoke: (deviceKeyId: string) => void,
  deviceKeyId: string,
  target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const onHide = () => {
    if (idb.memoryOnly) revoke(deviceKeyId)
  }
  target.addEventListener('pagehide', onHide)
  return () => target.removeEventListener('pagehide', onHide)
}
